import { ApolloProvider } from "@vue/apollo-option";

import { SalesforceApi, TObjectNode, TObjectNodeMetadata, TSalesforceObjectIdAndName } from "../api/salesforce/api";
import { IntegrationServiceEnum, TIntegrationObjectName } from "../types/integrations";
import { TSalesforceObjectName } from "../types/salesforce";

export class IntegrationObjectDataController {
  private static apolloProvider: ApolloProvider<unknown> | null = null;

  static initializeApolloProvider(apolloProvider: ApolloProvider<unknown>): void {
    this.apolloProvider = apolloProvider;
  }

  // Although specific to Salesforce, intended to support all services
  static async getNamesForObjectIds(
    service: IntegrationServiceEnum,
    objectName: TIntegrationObjectName,
    objectIds: string[]
  ): Promise<{ Id: string; Name: string }[]> {
    if (service !== IntegrationServiceEnum.SALESFORCE) {
      // Only Salesforce is supported, so return objectIds as the names
      return objectIds.map((id) => ({ Id: id, Name: id }));
    }
    const salesforceApi = new SalesforceApi(this.apolloProvider);

    if (objectName === "Event") {
      return (await salesforceApi.getEventSubjectsForIds(objectIds)).map((fieldValue) => ({
        Id: fieldValue.Id,
        Name: fieldValue.Subject,
      }));
    }

    const fieldValues: TSalesforceObjectIdAndName[] = await salesforceApi.getObjectNamesForIds(
      objectName as unknown as TSalesforceObjectName,
      objectIds
    );
    return fieldValues.map((fieldValue) => ({ Id: fieldValue.Id, Name: fieldValue.Name }));
  }

  static async getSalesforceObjectNodes(
    objectName: TIntegrationObjectName,
    objectIds: string[]
  ): Promise<TObjectNode[]> {
    const salesforceApi = new SalesforceApi(this.apolloProvider);

    let objectIdsWithNames: TSalesforceObjectIdAndName[] = [];
    if (objectName === "Event") {
      objectIdsWithNames = (await salesforceApi.getEventSubjectsForIds(objectIds)).map((fieldValue) => ({
        Id: fieldValue.Id,
        Name: fieldValue.Subject,
      }));
    } else {
      const fieldValues: TSalesforceObjectIdAndName[] = await salesforceApi.getObjectNamesForIds(
        objectName as unknown as TSalesforceObjectName,
        objectIds
      );
      objectIdsWithNames = fieldValues.map((fieldValue) => ({ Id: fieldValue.Id, Name: fieldValue.Name }));
    }

    const objectIdsWithMetadata: TObjectNodeMetadata[] = await salesforceApi.getSalesforceObjectsMetadata({
      objectIds,
      objectName,
    });

    return objectIds
      .map((objectId) => {
        const objectWithName = objectIdsWithNames.find((object) => object.Id === objectId);
        const objectWithMetadata = objectIdsWithMetadata.find((object) => object.id === objectId);
        if (!objectWithName || !objectWithMetadata) {
          return;
        }
        const objectNode: TObjectNode = {
          description: objectWithMetadata.description,
          id: objectId,
          name: objectWithName.Name,
          sobjectMetadata: objectWithMetadata.sobjectMetadata,
        };
        return objectNode;
      })
      .filter((objectNode) => objectNode !== undefined);
  }
}
